import React, { useState, useEffect, useRef, useCallback } from 'react';
import APIService from '../services/APIService';
import { MessageCircle, Send, X, Users, Clock } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { usePortal } from '../context/PortalContext';
import EmptyState from './EmptyState';

const ClientChatWidget = () => {
  const { user, isAdmin } = useAuth();
  const { unreadMessages, setUnreadMessages } = usePortal();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Set default admin for clients
  useEffect(() => {
    if (!isAdmin && open && !selectedAdmin) {
      loadAvailableAdmins();
    }
  }, [isAdmin, open, selectedAdmin]);

  useEffect(() => {
    if (!user) return;
    APIService.getUnreadMessageCount()
      .then((data) => setUnreadMessages(data?.unread_count || 0))
      .catch(() => setUnreadMessages(0));
  }, [user, setUnreadMessages]);

  const loadAvailableAdmins = async () => {
    try {
      const admins = await APIService.getAvailableAdmins();
      setAdminList(admins);
      
      // Select the first available admin
      if (admins && admins.length > 0) {
        setSelectedAdmin(admins[0].id);
      }
    } catch (error) {
      console.error('Failed to load available admins:', error);
      // Fallback: still try to use a basic admin lookup
    }
  };

  const loadChatHistory = useCallback(async () => {
    if (!selectedAdmin) return;
    
    setLoading(true);
    try {
      const history = await APIService.getChatHistory(selectedAdmin);
      setMessages(history || []);
      await APIService.markConversationRead(selectedAdmin);
      const unreadData = await APIService.getUnreadMessageCount().catch(() => ({ unread_count: 0 }));
      setUnreadMessages(unreadData?.unread_count || 0);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedAdmin, setUnreadMessages]);

  // Load chat history when admin is selected
  useEffect(() => {
    if (open && selectedAdmin) {
      loadChatHistory();
    }
  }, [open, selectedAdmin, loadChatHistory]);

  const sendMessage = async () => {
    if (!input.trim() || !selectedAdmin) return;
    
    const messageText = input.trim();
    setInput('');
    
    try {
      await APIService.sendChatMessage(selectedAdmin, messageText);
      // Reload chat history to show the new message
      await loadChatHistory();
      const unreadData = await APIService.getUnreadMessageCount().catch(() => ({ unread_count: 0 }));
      setUnreadMessages(unreadData?.unread_count || 0);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Restore the input if sending failed
      setInput(messageText);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.sent_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  if (!user) return null;

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        className={`fixed bottom-6 right-6 p-4 rounded-full shadow-lg z-50 transition-all duration-300 transform hover:scale-110 ${
          open 
            ? 'bg-red-500 hover:bg-red-600' 
            : 'bg-blue-600 hover:bg-blue-700'
        } text-white`}
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? (
          <X className="h-6 w-6" />
        ) : (
          <div className="relative">
            <MessageCircle className="h-6 w-6" />
            {/* Message indicator - could be enhanced to show unread count */}
            {unreadMessages > 0 ? (
              <span className="absolute -top-1 -right-1 min-w-[18px] rounded-full bg-red-500 px-1 text-center text-[10px] font-semibold text-white">
                {unreadMessages}
              </span>
            ) : null}
          </div>
        )}
      </button>

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-20 left-2 right-2 z-50 flex max-h-[70vh] flex-col rounded-xl border border-gray-200 bg-white shadow-xl sm:left-auto sm:right-6 sm:w-80 sm:max-w-[calc(100vw-3rem)]">
          {/* Chat Header */}
          <div className="p-4 border-b bg-blue-600 text-white rounded-t-xl flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span className="font-semibold">
                {isAdmin ? 'Client Support' : 
                 selectedAdmin && adminList.length > 0 
                   ? `Chat with ${adminList.find(a => a.id === selectedAdmin)?.name || 'Admin'}` 
                   : 'Chat with Admin'}
              </span>
            </div>
            <button 
              onClick={() => setOpen(false)}
              className="text-blue-100 hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] max-h-[400px]">
            {loading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : messages.length === 0 ? (
              <EmptyState
                icon={MessageCircle}
                title="No messages yet"
                description="Ask our team any question about your application and we will reply here."
                tone="soft"
              />
            ) : (
              Object.entries(groupedMessages).map(([date, dayMessages]) => (
                <div key={date}>
                  {/* Date separator */}
                  <div className="flex justify-center my-4">
                    <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                      {date}
                    </span>
                  </div>
                  
                  {/* Messages for this date */}
                  {dayMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${
                        msg.sender_id === user.id ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[75%] px-4 py-2 rounded-lg ${
                          msg.sender_id === user.id
                            ? 'bg-blue-600 text-white rounded-br-sm'
                            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                        }`}
                      >
                        <p className="break-words">{msg.content}</p>
                        <div
                          className={`flex items-center mt-1 text-xs ${
                            msg.sender_id === user.id
                              ? 'text-blue-100 justify-end'
                              : 'text-gray-500'
                          }`}
                        >
                          <Clock className="h-3 w-3 mr-1" />
                          {formatTime(msg.sent_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div className="p-4 border-t bg-gray-50 rounded-b-xl">
            <div className="flex space-x-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Type your message..."
                rows="1"
                style={{ minHeight: '38px', maxHeight: '100px' }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientChatWidget;
